<template>
  <BbPopup v-model="isOpened" :showCloseIcon="true" contentClass="custom-language-dialog">
    <template #popup-header>
      <TextBody
        tag="span"
        :i18n-key="isEditMode ? 'glossaries.customLanguage.editTitle' : 'glossaries.customLanguage.title'"
        :body-type="3"
        weight="medium"
      />
    </template>
    <div class="info-container">
      <BbInput
        v-model="name"
        placeholder=""
        :label="t('glossaries.customLanguage.name')"
        :is-required="true"
        size="small"
        :rules="rules"
        :autofocus="true"
      />
      <BbSelectList
        v-model="parentLanguage"
        :class="{ 'has-value': !!parentLanguage }"
        :label="t('glossaries.customLanguage.parentLanguage')"
        :is-required="true"
        :isAutocomplete="true"
        width="100%"
      >
        <template #default>
          <BbSelectListItem
            v-for="item in supportedLanguages"
            :key="item.language"
            :label="item.name"
            :value="item.language"
          >
            <div class="status-container">
              <BbIcon v-if="parentLanguage === item.language" size="small" name="checkmark-small" />
              <div class="bb-select-list-item__label">
                <span>{{ item.name }}</span>
                <span>{{ item.language }}</span>
              </div>
            </div>
          </BbSelectListItem>
        </template>
      </BbSelectList>
    </div>
    <template #popup-actions>
      <BbBtn type="secondary" @click="isOpened = false">
        <TextBody i18n-key="common.cancel" />
      </BbBtn>
      <BbBtn color="#6222e3" @click="create" :is-disabled="!canCreate">
        <TextBody :i18n-key="isEditMode ? 'common.update' : 'common.create'" />
      </BbBtn>
    </template>
  </BbPopup>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TextBody from '~/components/common/TextBody';

export default {
  components: { TextBody },
  props: {
    popupOpened: {
      type: Boolean,
      default: false,
    },
    supportedLanguages: {
      type: Array,
      default: () => [],
    },
    // names of the columns already in the glossary, a custom language needs its own name
    existingNames: {
      type: Array,
      default: () => [],
    },
    // custom column being edited ({ name, language }), the popup is in create mode when empty
    editing: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:open', 'execute:action'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const name = ref('');
    const parentLanguage = ref('');

    // The translated file and provider glossaries are keyed by a slug of the name (see buildCustomLanguageKey in the
    // backend), so only characters that survive the slug are accepted, and a name can't repeat another column's slug.
    const MAX_NAME_LENGTH = 40;
    const ALLOWED_NAME = /^[A-Za-z0-9 -]+$/;
    const toSlug = (value) =>
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    const isNameTaken = (value) => props.existingNames.some((existing) => toSlug(existing) === toSlug(value));

    const validateName = (value) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed) return t('modals.titleShouldNotBeEmpty');
      if (!ALLOWED_NAME.test(trimmed) || !toSlug(trimmed)) return t('glossaries.customLanguage.nameInvalid');
      if (trimmed.length > MAX_NAME_LENGTH) return t('glossaries.customLanguage.nameTooLong', { max: MAX_NAME_LENGTH });
      if (isNameTaken(trimmed)) return t('glossaries.customLanguage.nameTaken');
      return true;
    };

    const rules = [validateName];

    const isOpened = computed({
      get: () => props.popupOpened,
      set: (val) => emit('update:open', val),
    });

    const isEditMode = computed(() => !!props.editing);

    const hasChanges = computed(
      () =>
        !isEditMode.value ||
        name.value.trim() !== props.editing.name ||
        parentLanguage.value !== props.editing.language,
    );

    const canCreate = computed(() => validateName(name.value) === true && !!parentLanguage.value && hasChanges.value);

    const create = () => {
      emit('execute:action', { name: name.value.trim(), parentLanguage: parentLanguage.value });
    };

    // start with a clean form (or the edited column's values) every time the popup is reopened
    watch(isOpened, (opened) => {
      if (opened) {
        name.value = props.editing?.name ?? '';
        parentLanguage.value = props.editing?.language ?? '';
      }
    });

    return {
      canCreate,
      create,
      isEditMode,
      isOpened,
      name,
      parentLanguage,
      rules,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.info-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  // autocomplete mode hides the dropdown arrow, keep it visible
  :deep(.bb-select-list.autocomplete-on .el-select__suffix) {
    visibility: visible;
  }

  // the clear (x) icon takes the arrow's place once a value is picked
  :deep(.bb-select-list.has-value .el-select__suffix) {
    visibility: hidden;
  }

  // same item layout as the languages in the translation modal: name on top, code under it in grey
  .status-container {
    display: flex;
    gap: $bb-spacing-1;
    align-items: center;
  }

  .bb-select-list-item__label {
    @include bb-regular-text-styles('lg');
    color: $bb-neutral-1;
  }
}

.bb-select-list {
  :deep(.el-select-dropdown__item) {
    background-color: $bb-neutral-0;
    border: none !important;

    .status-container .bb-select-list-item {
      &__label {
        display: flex;
        flex-direction: column;

        > span:nth-of-type(2) {
          @include bb-regular-text-styles('xs');
          color: $bb-neutral-2;
        }
      }
    }

    &.is-selected {
      color: $bb-primary-3 !important;
      background-color: $bb-neutral-0 !important;

      .status-container .bb-select-list-item {
        &__label {
          color: $bb-primary-3 !important;
        }
      }
    }
  }
}
</style>
